'use client';
import React, { Suspense, useEffect, useState } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { useRouter } from 'next/navigation';
import Banner from '@/components/Banner';
import BodyBox from '@/components/BodyBox';
import Calendar, { BOOKING_WINDOW_DAYS, bookingWindowMonths } from '@/components/Calendar';
import CourseItemBox from '@/components/CourseItemBox';
import DestinationInfoBox from '@/components/DestinationInfoBox';
import RoomTypeList from '@/components/RoomTypeList';
import TabInfoBox from '@/components/TabInfoBox';
import { API } from '@/app/api/config';
import { customAxios } from '@/modules/common/api';
import moment from 'moment';
import Image from 'next/image';
import SweetAlert from 'sweetalert2';
import { addDays } from 'date-fns';
import Loading from '@/components/common/Loading';
import SwiperBox from '@/components/SwiperBox';
import useWindowWidth from '@/hooks/useWindowWidth';
import useDeviceSize from '@/hooks/useDeviceSize';
import xss from 'xss';
import SwiperClass from 'swiper/types/swiper-class';
import { makeNumPinUrl } from '@/modules/utils/makeNumPinUrl';
import { useHeadcount } from '@/modules/search/HeadcountContext';
import { parseJsonParam } from '@/modules/common/url-params';

interface PartnerStoreData {
  storeName?: string;
  storeEnglishName?: string;
  address?: string;
  storyContent?: string;
}

interface TouristSpot {
  name?: string;
  address?: string;
  distance?: number;
  description?: string;
  title?: string;
  latitude: number;
  longitude: number;
}

interface FeatureItem {
  title?: string;
  content?: string;
  image?: { imageUrl?: string };
}

interface RoomPriceItem {
  id: string | number;
  isReservationAvailability: boolean;
  imageUrl?: string;
  name?: string;
  discountPrice?: number;
  roomRate?: number;
  description?: string;
  standardPeopleCount?: number;
  maximumPersonnelCount?: number;
  squareMeter?: number;
  bedSingleSizeCount?: number;
  bedQueenSizeCount?: number;
  bedKingSizeCount?: number;
  checkIn?: string;
  checkOut?: string;
  roomTypeVo?: { maximumPersonnelCount?: number };
  blockTitle?: string;
}

interface ReservationAvailabilityItem {
  reservationCount: number;
  roomCount: number;
  date: string;
}

type ReservationDateSelection = { start: string; end: string; scrollPosition?: number };

type StoreMarker = kakao.maps.Marker & { spotId: number; spotData?: TouristSpot };

// 주소의 header-headcount는 { adult, kid, pet } 형태지만, 이 페이지 안에서는 { adult, child, pet } 형태로 쓴다.
// 모양을 맞추지 않으면 새로고침·공유 링크로 열었을 때 아동 인원이 항상 0으로 읽힌다.
const toInternalHeadcount = (urlHeadcount: { adult?: number; kid?: number; pet?: number } | null) =>
  urlHeadcount ? { adult: urlHeadcount.adult, child: urlHeadcount.kid, pet: urlHeadcount.pet } : urlHeadcount;

const StayContent = ({ params }: { params: { stayId: string } }) => {
  const pathName = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();

  const width = useWindowWidth();
  const headerPeriod = parseJsonParam(searchParams?.get('header-period'));

  // 인원수는 페이지 간 공유되는 HeadcountContext를 우선 사용한다.
  // (뒤로가기로 돌아온 페이지가 자기 자신의 오래된 URL을 다시 읽어 인원수가 되돌아가는 문제 방지)
  const { headcount: sharedHeadcount, isInitialized: isHeadcountInitialized, setHeadcount: setSharedHeadcount } = useHeadcount();

  const [headerHeadcount, setHeaderHeadcount] = useState(
    isHeadcountInitialized
      ? { adult: sharedHeadcount.adult, child: sharedHeadcount.kid, pet: sharedHeadcount.pet }
      : toInternalHeadcount(parseJsonParam(searchParams?.get('header-headcount'))),
  );
  const stayId = pathName.split('/')[3];

  const scrollPositionKey = `scrollPosition_${stayId}`;

  const [partnerStoreData, setPartnerStoreData] = useState<PartnerStoreData | null>(null);
  const [maximumPerson, setMaximumPerson] = useState(0); //최대인원
  const [maximumPet, setMaximumPet] = useState(0); // 최대 반려견 수
  const [topImage, setTopImage] = useState<{ imageUrl?: string } | null>(null);
  const [featureData, setFeatureData] = useState<FeatureItem[]>([]);
  const [touristSpotData, setTouristSpotData] = useState<TouristSpot[]>([]);
  const [storyData, setStoryData] = useState<{ storyTitle?: string; storyContent?: string } | undefined>();
  const [roomList, setRoomList] = useState<RoomPriceItem[]>([]);
  const [stayCount, setStayCount] = useState(1);
  const [guideData, setGuideData] = useState<unknown[]>([]);
  const [refundPolicyInfoList, setRefundPolicyInfoList] = useState<unknown[]>([]);
  const [isPet, setIsPet] = useState(false);

  // 카운팅 숫자
  const [adultCount, setAdultCount] = useState<number>(isHeadcountInitialized ? sharedHeadcount.adult : 1);
  const [kidCount, setKidCount] = useState(isHeadcountInitialized ? sharedHeadcount.kid : 0);
  const [petCount, setPetCount] = useState(isHeadcountInitialized ? sharedHeadcount.pet : 0);
  const [showEditBox, setShowEditBox] = useState(false);
  const [isOpenCalendar, setIsOpenCalendar] = useState(false);
  const [partnerStoreImages, setPartnerStoreImages] = useState<unknown[]>([]);

  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);

  const [swiperIndex, setSwiperIndex] = useState(0); // -> 페이지네이션용
  const [swiper, setSwiper] = useState<SwiperClass>(); // -> 슬라이드용

  const [disabledDates, setDisabledDates] = useState<Date[]>([]);
  // 숙소 예약가능일수(관리자 운영 기본값). 숙소 정보를 받기 전에는 기본값으로 달력·마감 조회를 한다.
  const [availableDays, setAvailableDays] = useState<number>(BOOKING_WINDOW_DAYS);


  //let isMapLoaded = false;


  const [kakaoMap, setKakaoMap] = useState<kakao.maps.Map | null>(null);
  const [mapMarkers, setMapMarkers] = useState<StoreMarker[]>([]);
  const [storeCenter, setStoreCenter] = useState<kakao.maps.LatLng | null>(null);


useEffect(() => {
  // 스크롤 위치 키 설정
  const scrollPositionKey = `scrollPosition_${stayId}`;

  // 브라우저의 자동 스크롤 복원 비활성화
  if ('scrollRestoration' in window.history) {
    window.history.scrollRestoration = 'manual';
  }

  // 이전에 저장된 스크롤 위치가 있으면 복원
  const savedPosition = sessionStorage.getItem(scrollPositionKey);
  if (savedPosition) {
    setTimeout(() => {
      window.scrollTo({
        top: parseInt(savedPosition),
        behavior: 'auto'
      });
      console.log('스크롤 위치 복원:', parseInt(savedPosition));
    }, 300);
  }

  // 스크롤 위치 저장 함수
  const saveScrollPosition = () => {
    const currentPosition = window.scrollY;
    if (currentPosition > 0) {
      sessionStorage.setItem(scrollPositionKey, currentPosition.toString());
      console.log('스크롤 위치 저장:', currentPosition);
    }
  };

  // 스크롤 이벤트 리스너 (디바운스 적용)
  let scrollDebounce: ReturnType<typeof setTimeout> | undefined;
  const handleScroll = () => {
    clearTimeout(scrollDebounce);
    // 스크롤이 멈춘 후 500ms 후에만 저장
    scrollDebounce = setTimeout(saveScrollPosition, 500);
  };

  window.addEventListener('scroll', handleScroll, { passive: true });

  // 페이지 이동 시 저장 (필수 이벤트)
  window.addEventListener('beforeunload', saveScrollPosition);

  return () => {
    // 정리 작업
    clearTimeout(scrollDebounce);
    window.removeEventListener('scroll', handleScroll);
    window.removeEventListener('beforeunload', saveScrollPosition);

    // 컴포넌트 언마운트 시 최종 스크롤 위치 저장
    saveScrollPosition();

    // 원래 브라우저 설정으로 복원
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'auto';
    }
  };
}, [stayId, router]);

  const initializeKakaoMap = (latitude: number, longitude: number, touristSpots: TouristSpot[]) => {

    //if (!window.kakao || !document.getElementById('map')) return;

    window.kakao.maps.load(() => {
      const container = document.getElementById('map');
      if (!container) return;

      // Only create a new map if one doesn't exist
      //if (!kakaoMap) {
        console.log(' kakaoMap load');
        const center = new kakao.maps.LatLng(latitude, longitude);
        setStoreCenter(center);
        const options = { center, level: 3 };
        const newMap = new kakao.maps.Map(container, options);
        setKakaoMap(newMap);

        // Add all markers
        addAllMarkersToMap(newMap, latitude, longitude, touristSpots);

      //}
    });
  };

  // Function to add all markers to map
  const addAllMarkersToMap = (map: kakao.maps.Map, storeLatitude: number, storeLongitude: number, touristSpots: TouristSpot[]) => {
    const newMarkers: StoreMarker[] = [];

    // Add the main store marker (star)
    const storePosition = new kakao.maps.LatLng(storeLatitude, storeLongitude);
    const storeMarker = new kakao.maps.Marker({
      map,
      position: storePosition,
      image: new kakao.maps.MarkerImage(
        'https://t1.daumcdn.net/localimg/localimages/07/mapapidoc/markerStar.png',
        new kakao.maps.Size(24, 35),
      ),
    }) as StoreMarker;
    storeMarker.spotId = 0;
    newMarkers.push(storeMarker);

    // Add number markers for tourist spots
    touristSpots.forEach((spot, idx) => {
      const pinSize = { w: 28, h: 36 };
      const pinUrl = makeNumPinUrl(idx + 1, '#FF673E', '#FFFFFF', pinSize.w, pinSize.h);

      const spotPosition = new kakao.maps.LatLng(spot.latitude, spot.longitude);
      const marker = new kakao.maps.Marker({
        map,
        position: spotPosition,
        title: spot.title,
        image: new kakao.maps.MarkerImage(
          pinUrl,
          new kakao.maps.Size(pinSize.w, pinSize.h),
          { offset: new kakao.maps.Point(pinSize.w / 2, pinSize.h) },
        ),
      }) as StoreMarker;
      marker.spotId = idx + 1;
      marker.spotData = spot;
      newMarkers.push(marker);
    });

    setMapMarkers(newMarkers);
  };



  const onSwiper = (data: SwiperClass) => {
    setSwiper(data);
  };

  const onSwiperIndex = (data: number) => {
    setSwiperIndex(data);
  };


// 반려견 추가 (별도 최대인원 체크)
const petPlusCountHandler = () => {
  if (Number(petCount) >= maximumPet) {
    SweetAlert.fire({
      text: '입실 가능한 반려견 수를 초과했습니다.',
      icon: 'warning',
      showConfirmButton: true,
    }).then((result) => {
      if (result.isConfirmed) {
        return;
      }
    });
  } else {
    setPetCount((prevpetCount) => Number(prevpetCount) + 1);
  }
};

  const selectPartnerStore = async () => {
    // 현재 호스트(도메인 또는 IP) 확인
    const host = window.location.host;

    console.log("host : " + host);

    // 운영 서버만 실행 (예: stayinterview.co.kr)
    const isTestServer = host.includes('stayinterview.co.kr');

    console.log("isTestServer : " + isTestServer);

    if (!isTestServer) {
      console.log('개발/테스트 환경 - 예약 제한 로직 실행 안함');
      return;
    }

    // TODO 테스트할때 주석처리(반영할때 주의하자!!)
    await customAxios.get(`${API.PARTNER_STORE}/${stayId}`).then((res) => {
      if (res.data.response.partnerStoreStatus !== 'NORMAL') {
        SweetAlert.fire({
          text: '해당 스테이는 현재 예약할 수 없습니다.',
          icon: 'warning',
          showConfirmButton: true,
        }).then((result) => {
          if (result.isConfirmed) {
            router.push('/');
            // return;
          }
          router.push('/');
        });
      }
    });
  };

  const [loading, setLoading] = useState(false);
  const isDesktop = useDeviceSize();

  const selectRoomRateList = async () => {
    let roomParams = {};
    // let startDate = null;
    // let endDate = null;
    let diff = 1;
    let adult = headerHeadcount?.adult ?? 1;
    let kid = headerHeadcount?.child ?? 0;
    let pet = headerHeadcount?.pet ?? 0;

    const today = new Date();
    let newStartDate = moment(today).format('YYYY-MM-DD');
    let newEndDate = moment(addDays(today, 1)).format('YYYY-MM-DD');

    if (startDate != null) {
      diff = Math.ceil(
        Math.abs(new Date(startDate).getTime() - new Date(endDate!).getTime()) /
          (1000 * 60 * 60 * 24),
      );
      roomParams = {
        partnerStoreId: stayId,
        startDate: headerPeriod != null ? headerPeriod.start : newStartDate,
        endDate: headerPeriod != null ? headerPeriod.end : newEndDate,
        adultCount: adult,
        kidCount: kid,
        petCount: pet,
        diffDays: diff,
      };
    } else {
      setStartDate(newStartDate);
      setEndDate(newEndDate);

      roomParams = {
        partnerStoreId: stayId,
        startDate: headerPeriod != null ? headerPeriod.start : newStartDate,
        endDate: headerPeriod != null ? headerPeriod.end : newEndDate,
        adultCount: adult,
        kidCount: kid,
        petCount: pet,
        diffDays: diff,
      };
    }

    let diffStartDate = '';
    let diffEndDate = '';

    if (headerPeriod != null) {
      diffStartDate = headerPeriod.start;
      diffEndDate = headerPeriod.end;
    } else {
      diffStartDate = moment(today).format('YYYY-MM-DD');
      diffEndDate = moment(addDays(today, 1)).format('YYYY-MM-DD');
    }

    diff = Math.ceil(
      Math.abs(new Date(diffStartDate).getTime() - new Date(diffEndDate).getTime()) /
        (1000 * 60 * 60 * 24),
    );

    setStayCount(diff);

    await customAxios.get(`${API.ROOM_PRICE}`, { params: roomParams }).then((res) => {
      setRoomList(res?.data?.response?.data);
      let newRoomList: RoomPriceItem[] = [];

      res?.data?.response?.data.map((item: RoomPriceItem) => {
        //최대인원이 선택인원보다 적으면 예약 불가능하게 바꿔야함

        let isReservationAvailability = true;
        if (item.isReservationAvailability == true) {
          if (adult + kid > (item.roomTypeVo?.maximumPersonnelCount ?? Infinity)) {
            isReservationAvailability = false;
            item.blockTitle = '입실인원 초과';

          }
        } else {
          item.blockTitle = '해당날짜 예약마감';
        }

        if (item.isReservationAvailability === false) {
          isReservationAvailability = false;
        }

        newRoomList.push({
          ...item,
          isReservationAvailability,
        });
      });

      setRoomList(newRoomList);

      setShowEditBox(false);
    }).catch((error) => {
      // 실패해도 아무 안내가 없으면 객실이 없는 숙소로 보여 사용자가 그냥 떠나게 된다.
      console.error('selectRoomRateList error:', error);
      SweetAlert.fire({
        text: '객실 정보를 불러오지 못했습니다. 잠시 후 다시 시도해주세요.',
        icon: 'warning',
        showConfirmButton: true,
      });
    });
  };

  useEffect(() => {
  // 펫 허용되는 스테이 ID 리스트
  //const petAllowedStayIds = ['46', '42']; // 원하는 ID 추가
  //setIsPet(petAllowedStayIds.includes(stayId));

  selectPartnerStore();
  if (headerPeriod != null) {
    setStartDate(headerPeriod?.start);
    setEndDate(headerPeriod?.end);

    selectRoomRateList();
  } else {
    selectRoomRateList();
  }
}, []);

  // 처음엔 기본값으로, 숙소의 예약가능일수가 도착해 값이 바뀌면 그 기간으로 마감 정보를 다시 받는다.
  useEffect(() => {
    selectReservationAvailability();
  }, [availableDays]);

  useEffect(() => {
    selectPartnerStoreDetail();
  }, [loading]);

  useEffect(() => {
    setAdultCount(headerHeadcount?.adult != null ? headerHeadcount.adult : 1);
    setKidCount(headerHeadcount?.child != null ? headerHeadcount.child : 0);
    setPetCount(headerHeadcount?.pet != null ? headerHeadcount.pet : 0);
  }, [headerHeadcount]);

  const selectPartnerStoreImages = async () => {
    await customAxios.get(`${API.PARTNER_STORE}/${stayId}/store-images`).then((res) => {
      setPartnerStoreImages(res?.data?.response);
    });
  };

  const selectPartnerStoreDetail = async () => {
    await customAxios.get(`${API.PARTNER_STORE}/${stayId}`).then((res) => {
      if (res !== undefined && res?.status === 200) {
        setPartnerStoreData(res.data.response);
        setAvailableDays(res.data.response.availableDays || BOOKING_WINDOW_DAYS);
        setMaximumPerson(res.data.response.maximumPerson);
        setGuideData(res.data.response.guideInfoList);
        setRefundPolicyInfoList(res.data.response.refundPolicyInfoList);
		//setIsPet(res.data.response.isPetAllowed || false);
        setIsPet(res.data.response.isPetStay || false);
        setTopImage(res.data.response.topImage);

        setMaximumPet(res.data.response.maximumPet ?? Infinity);

        const kakaoMapScript = document.createElement('script');
        kakaoMapScript.async = true;
        kakaoMapScript.src = `//dapi.kakao.com/v2/maps/sdk.js?appkey=${process.env.NEXT_PUBLIC_KAKAOMAP_APPKEY}&autoload=false`;
        document.head.appendChild(kakaoMapScript);

         kakaoMapScript.addEventListener('load', () => {
                initializeKakaoMap(
                  res.data.response.latitude,
                  res.data.response.longitude,
                  res.data.response.touristSpotList.data
                );
         });




//         const onLoadKakaoAPI = () => {
//           window.kakao.maps.load(() => {
//             const container = document.getElementById('map');
//             const options = {
//               center: new window.kakao.maps.LatLng(
//                 res.data.response.latitude,
//                 res.data.response.longitude,
//               ),
//               level: 3,
//             };
//             const map = new window.kakao.maps.Map(container, options);
//
//             /* ① 숙소(★) 기본 마커 */
//             const homePos = new kakao.maps.LatLng(
//               res.data.response.latitude,
//               res.data.response.longitude,
//             );
//             const starImg =
//               'https://t1.daumcdn.net/localimg/localimages/07/mapapidoc/markerStar.png';
//             new kakao.maps.Marker({
//               map,
//               position: homePos,
//               image: new kakao.maps.MarkerImage(starImg, new kakao.maps.Size(24, 35)),
//             });
//
//             /* ② 숫자 마커 헬퍼 */
//             const makeNumberMarker = (
//               pos: kakao.maps.LatLng,
//               num: number,
//             ) => {
//               const content = `<div class="number-marker">${num}</div>`;
//               return new kakao.maps.CustomOverlay({
//                 map,
//                 position: pos,
//                 content,
//                 yAnchor: 1,
//               });
//             };
//
//             /* ③ 관광지에 번호 부여 */
//             res.data.response.touristSpotList.data.forEach(
//               (spot: any, idx: number) => {
//                 makeNumberMarker(
//                   new kakao.maps.LatLng(spot.latitude, spot.longitude),
//                   idx + 1,        // 1부터 시작
//                 );
//               },
//             );
//
//             map.setCenter(homePos);
//           });
//         };
        //kakaoMapScript.addEventListener('load', onLoadKakaoAPI);

        setStoryData({
          storyTitle: res.data.response.storyTitle,
          storyContent: res.data.response.storyContent,
        });
        setFeatureData(res.data.response.storeFeatureList.data);
        setTouristSpotData(res.data.response.touristSpotList.data);
      }
    }).catch((error) => {
      // 없는 숙소(404)·서버 오류 등으로 상세를 못 불러오면 화면이 무한 로딩에 갇히므로 목록으로 보낸다.
      // 서버 응답이 있는 오류는 공통 인터셉터가 안내 팝업을 띄우므로, 응답이 없는(네트워크) 경우만 직접 안내한다.
      if (!error?.response) {
        SweetAlert.fire({
          text: '숙소 정보를 불러오지 못했습니다. 다시 시도해주세요.',
          icon: 'warning',
          showConfirmButton: true,
        });
      }
      router.replace('/ourStay');
    });

    if (startDate == null && endDate == null) {
      if (headerPeriod != null) {
        // 주소로 기간이 넘어온 경우(새로고침·공유 링크·헤더 달력 선택)에는 오늘 날짜로 덮어쓰지 않고 그 기간을 쓴다.
        // 이 함수는 첫 렌더의 값(null)을 기억한 채 실행돼서, 이미 설정한 기간을 보지 못하고 오늘로 되돌려 버렸다.
        setStartDate(headerPeriod.start);
        setEndDate(headerPeriod.end);
      } else {
        let today = new Date();
        setStartDate(moment(today).format('YYYY-MM-DD'));
        setEndDate(moment(addDays(today, 1)).format('YYYY-MM-DD'));
      }
    } else {
      let diff = Math.ceil(
        Math.abs(
          new Date(endDate!).getTime() - new Date(startDate!).getTime(),
        ) /
          (1000 * 60 * 60 * 24),
      );
      setStayCount(diff);
    }
  };

  useEffect(() => {
    selectPartnerStoreImages();
    //selectPartnerStoreDetail();
    moveToStoreCenter();
  }, [isDesktop]);

  const moveToStoreCenter=()=>{
        if (!kakaoMap || !storeCenter) return;
          kakaoMap.panTo(storeCenter);
  };

  const handleEditClick = () => {
     setShowEditBox(!showEditBox); // 토글 방식으로 변경
     setIsOpenCalendar(!isOpenCalendar);
  };

  const adultPlusCountHandler = () => {
    if (Number(adultCount) + Number(kidCount) >= maximumPerson) {
      SweetAlert.fire({
        text: '입실 가능한 인원을 초과했습니다.',
        icon: 'warning',
        showConfirmButton: true,
      }).then((result) => {
        if (result.isConfirmed) {
          return;
        }
      });
    } else {
      setAdultCount((prevadultCount) => Number(prevadultCount) + 1);
    }
  };

  const adultMinusCountHandler = () => {
    if (Number(adultCount) > 0) {
      // 음수로 가지 않도록 확인합니다.
      setAdultCount((prevadultCount) => Number(prevadultCount) - 1);
    }
  };

  const kidPlusCountHandler = () => {
    if (Number(adultCount) + Number(kidCount)  >= maximumPerson) {
      SweetAlert.fire({
        text: '입실 가능한 인원을 초과했습니다.',
        icon: 'warning',
        showConfirmButton: true,
      }).then((result) => {
        if (result.isConfirmed) {
          return;
        }
      });
    } else {
      setKidCount((prevkidCount) => Number(prevkidCount) + 1);
    }
  };

  const kidMinusCountHandler = () => {
    if (Number(kidCount) > 0) {
      // 음수로 가지 않도록 확인합니다.
      setKidCount((prevkidCount) => Number(prevkidCount) - 1);
    }
  };

  const handleReSelectClick = async () => {

      const today = new Date();
      let newStartDate = moment(today).format('YYYY-MM-DD');
      let newEndDate = moment(addDays(today, 1)).format('YYYY-MM-DD');


      let currentStartDate = headerPeriod != null ? headerPeriod.start : startDate!=null ? startDate : newStartDate;
      let currentEndDate = headerPeriod != null ? headerPeriod.end : endDate!=null ? endDate : newEndDate;


    if (currentStartDate == currentEndDate) {
      SweetAlert.fire({
        html: `데이유즈(공간임대) 예약 시 이용하실 날짜와 다음날을<br>함께 선택하여 예약해 주세요.
                 <br>(예: 4월 10일 이용 시 -> 4월 10일 ~ 4월 11일 선택)`,
        icon: 'warning',
        showConfirmButton: true,
      }).then((result) => {
        if (result.isConfirmed) {
          // Update the dates
          setStartDate(newStartDate);
          setEndDate(newEndDate);

          // Call the shared method
          updateRoomListAndParams(newStartDate, newEndDate, adultCount, kidCount, petCount);
        }
      });
      return;
    }
    setStartDate(currentStartDate);
    setEndDate(currentEndDate);

    // Call the shared method
    updateRoomListAndParams(currentStartDate, currentEndDate, adultCount, kidCount, petCount);
  };

//   const handleReSelectClick = async () => {
//   if (startDate == endDate) {
//     SweetAlert.fire({
//       text: '체크인 날짜와 체크아웃 날짜가 같습니다.',
//       icon: 'warning',
//       showConfirmButton: true,
//     }).then((result) => {
//       if (result.isConfirmed) {
//         const today = new Date();
//
//         let startDate = moment(today).format('YYYY-MM-DD');
//         let endDate = moment(addDays(today, 1)).format('YYYY-MM-DD');
//
//         // URL 파라미터 변경 (새로고침 없이)
//         updateUrlParams(startDate, endDate, adultCount, kidCount, petCount);
//       }
//     });
//     return;
//   }
//
//   setLoading(true);
//
//   let diff = Math.abs(
//     new Date(endDate.toString()).getTime() - new Date(startDate.toString()).getTime(),
//   );
//   diff = Math.ceil(diff / (1000 * 60 * 60 * 24));
//
//   const roomParams = {
//     partnerStoreId: stayId,
//     startDate: startDate,
//     endDate: endDate,
//     adultCount,
//     kidCount,
//     petCount,
//     diffDays: diff,
//   };
//
//   setStayCount(diff);
//
//   await customAxios.get(`${API.ROOM_PRICE}`, { params: roomParams }).then((res) => {
//     setRoomList(res?.data?.response?.data);
//     let newRoomList: Array<any> = [];
//
//     res?.data?.response?.data.map((item: any) => {
//       //최대인원이 선택인원보다 적으면 예약 불가능하게 바꿔야함
//
//       let isReservationAvailability = true;
//       if (item.isReservationAvailability == true) {
//         if (adultCount + kidCount  > item.roomTypeVo?.maximumPersonnelCount) {
//           isReservationAvailability = false;
//           item.blockTitle = '입실인원 초과';
//         }
//       } else {
//         item.blockTitle = '해당날짜 예약마감';
//       }
//
//       if (item.isReservationAvailability === false) {
//         isReservationAvailability = false;
//       }
//
//       newRoomList.push({
//         ...item,
//         isReservationAvailability,
//       });
//     });
//
//     setRoomList(newRoomList);
//     setShowEditBox(false);
//
//     // URL 파라미터 변경 (새로고침 없이)
//     updateUrlParams(startDate, endDate, adultCount, kidCount, petCount);
//
//     setLoading(false);
//   });
// };

const updateUrlParams = (startDate: string, endDate: string, adultCount: number, kidCount: number, petCount: number, scrollPosition: number = window.scrollY) => {
  // 헤더값 업데이트
  setHeaderHeadcount({
    adult: adultCount,
    child: kidCount,
    pet: petCount,
  });
  setSharedHeadcount({
    adult: Number(adultCount),
    kid: Number(kidCount),
    pet: Number(petCount),
  });

  // URL 파라미터 설정
  const periodParam = JSON.stringify({ start: startDate, end: endDate });
  const headcountParam = JSON.stringify({
    adult: adultCount,
    kid: kidCount,
    pet: petCount,
  });
  
  // 현재 URL에서 파라미터만 업데이트 (router.replace 사용)
  router.replace(
    `${pathName}?header-period=${periodParam}&header-headcount=${headcountParam}`,
    { scroll: false } // 스크롤 위치 유지 옵션
  );
  
  // 스크롤 위치 복원을 위한 setTimeout
//   setTimeout(() => {
//     window.scrollTo({
//       top: scrollPosition,
//       behavior: 'auto'
//     });
//   }, 100);
};

  const onCalendarClose = () => {
    setShowEditBox(true);
  };

  const onCalendarOpen = () => {
    setShowEditBox(false);
  };

  const updateRoomListAndParams = async (startDateData: string, endDateData: string, adultCount: number, kidCount: number, petCount: number, scrollPosition: number = window.scrollY) => {
    setLoading(true);

    let diff = Math.abs(
      new Date(endDateData).getTime() - new Date(startDateData).getTime(),
    );
    diff = Math.ceil(diff / (1000 * 60 * 60 * 24));

    const roomParams = {
      partnerStoreId: stayId,
      startDate: startDateData,
      endDate: endDateData,
      adultCount,
      kidCount,
      petCount,
      diffDays: diff,
    };

    setStayCount(diff);

    try {
      const res = await customAxios.get(`${API.ROOM_PRICE}`, { params: roomParams });
      let newRoomList: RoomPriceItem[] = [];

      res?.data?.response?.data.map((item: RoomPriceItem) => {
        // Check if max occupancy exceeded
        let isReservationAvailability = true;
        if (item.isReservationAvailability == true) {
          if (adultCount + kidCount > (item.roomTypeVo?.maximumPersonnelCount ?? Infinity)) {
            isReservationAvailability = false;
            item.blockTitle = '입실인원 초과';
          }
        } else {
          item.blockTitle = '해당날짜 예약마감';
        }

        if (item.isReservationAvailability === false) {
          isReservationAvailability = false;
        }

        newRoomList.push({
          ...item,
          isReservationAvailability,
        });
      });

      setRoomList(newRoomList);

      // Update URL parameters (without page refresh)
      updateUrlParams(startDateData, endDateData, adultCount, kidCount, petCount, scrollPosition);

      // Close the edit box
      setShowEditBox(false);

    } catch (error) {
      console.error('Error fetching room rates:', error);
    } finally {
      setLoading(false);
    }
  };

  // 다른 탭/창에 있다가 이 탭으로 돌아왔을 때 요금을 다시 조회한다.
  // (어드민에서 가격을 바꿔도 이미 열려있는 탭은 새로고침 전까진 반영이 안 되던 문제)
  useEffect(() => {
    const handleFocus = () => {
      if (!startDate || !endDate) return;
      updateRoomListAndParams(startDate, endDate, adultCount, kidCount, petCount);
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [startDate, endDate, adultCount, kidCount, petCount]);

  const selectReservationAvailability = async () => {
    // 달력에서 고를 수 있는 마지막 날이 속한 달까지 받는다(두 달만 받으면 그 뒤는 만실도 열려 보인다).
    const paramList = bookingWindowMonths(availableDays).map(({ year, month }) => ({
      partnerStoreId: stayId,
      year,
      month,
    }));

    // 달력의 마감 날짜 표시용이라 실패해도 안내하지 않고(객실 조회 실패 안내와 겹치지 않게) 마감 표시만 비운 채 진행한다.
    try {
      const reservationAvailableDateList = await customAxios.post(
        `${API.RESERVATION_AVAILABLE_DATE}`,
        paramList,
      );

      let disabledDates: Date[] = [];
      reservationAvailableDateList?.data?.response.forEach((item: ReservationAvailabilityItem) => {
        if (item.reservationCount >= item.roomCount) {
          disabledDates.push(new Date(item.date));
        }
      });

      setDisabledDates(disabledDates);
    } catch (error) {
      console.error('selectReservationAvailability error:', error);
    }
  };

  const handleSelectTour = (item: TouristSpot, markerIndex: number) => {
    if (!kakaoMap) return;
    console.log(`markerIndex:${markerIndex}`)
  // 선택된 관광지로 지도 이동
  const spotPosition = new kakao.maps.LatLng(item.latitude, item.longitude);
  kakaoMap.panTo(spotPosition);

 if (markerIndex !== -1) {
   // 선택된 마커만 z-index를 높게 설정
   mapMarkers.forEach((marker, idx) => {
     marker.setZIndex(idx === markerIndex ? 10 : 1);
    });
  }



  };


  const petMinusCountHandler = () => {
    if (Number(petCount) > 0) {
      // 음수로 가지 않도록 확인합니다.
      setPetCount((prevpetCount) => Number(prevpetCount) - 1);
    }
  };
 const clickReservationDate = async (data: ReservationDateSelection) => {
   let startDateData = data.start;
   let endDateData = data.end;
   const scrollPosition = data.scrollPosition || window.scrollY;
//  partnerStoreData?.storeName
   // 체크인/체크아웃 날짜가 같은 경우 처리
   if (startDateData === endDateData) {
     SweetAlert.fire({
       html: `데이유즈(공간임대) 예약 시 이용하실 날짜와 다음날을<br>함께 선택하여 예약해 주세요.
               <br>(예: 4월 10일 이용 시 -> 4월 10일 ~ 4월 11일 선택)`,
       icon: 'warning',
       showConfirmButton: true,
     }).then((result) => {
       if (result.isConfirmed) {
         const today = new Date();
         let newStartDate = moment(today).format('YYYY-MM-DD');
         let newEndDate = moment(addDays(today, 1)).format('YYYY-MM-DD');

         // 날짜 업데이트
         setStartDate(newStartDate);
         setEndDate(newEndDate);

         // 공통 메소드 호출
         updateRoomListAndParams(newStartDate, newEndDate, adultCount, kidCount, petCount, scrollPosition);
       }
     });
     return;
   }

   setStartDate(startDateData);
   setEndDate(endDateData);

   // 공통 메소드 호출
   await updateRoomListAndParams(startDateData, endDateData, adultCount, kidCount, petCount, scrollPosition);

   // 달력 닫기
   setIsOpenCalendar(false);
 };

//  const clickReservationDate = async (data: any) => {
//   let startDateData = data.start;
//   let endDateData = data.end;
//   // 전달받은 스크롤 위치 (있는 경우)
//   const scrollPosition = data.scrollPosition || window.scrollY;
//
//   setStartDate(startDateData);
//   setEndDate(endDateData);
//
//   setLoading(true);
//
//   const roomParams = {
//     partnerStoreId: stayId,
//     startDate: startDateData,
//     endDate: endDateData,
//     adultCount,
//     kidCount,
//     petCount,
//   };
//
//   let diff = Math.abs(
//     new Date(endDateData).getTime() - new Date(startDateData).getTime(),
//   );
//   diff = Math.ceil(diff / (1000 * 60 * 60 * 24));
//
//   setStayCount(diff);
//
//   try {
//     const res = await customAxios.get(`${API.ROOM_PRICE}`, { params: roomParams });
//     let newRoomList: Array<any> = [];
//
//     res?.data?.response?.data.map((item: any) => {
//       //최대인원이 선택인원보다 적으면 예약 불가능하게 바꿔야함
//       let isReservationAvailability = true;
//       if (item.isReservationAvailability == true) {
//         if (adultCount + kidCount > item.roomTypeVo?.maximumPersonnelCount) {
//           isReservationAvailability = false;
//           item.blockTitle = '입실인원 초과';
//         }
//       } else {
//         item.blockTitle = '해당날짜 예약마감';
//       }
//
//       if (item.isReservationAvailability === false) {
//         isReservationAvailability = false;
//       }
//
//       newRoomList.push({
//         ...item,
//         isReservationAvailability,
//       });
//     });
//
//     setRoomList(newRoomList);
//
//     // URL 파라미터 변경 (새로고침 없이) - 현재 스크롤 위치 유지
//     updateUrlParams(startDateData, endDateData, adultCount, kidCount, petCount, scrollPosition);
//
//     // 달력을 닫습니다
//     setIsOpenCalendar(false);
//   } catch (error) {
//     console.error('Error fetching room rates:', error);
//   } finally {
//     setLoading(false);
//   }
// };

const getSlidesPerView = () => {
    if (width > 1600) return 3;
    if (width > 1300) return 2;
    if (width > 1000) return 1.8;
    if (width > 768) return 1.5;
    return 1;
  };

  return (
    <div>
      <Banner data={partnerStoreImages} enableAutoplay={true} autoplayDelay={4000} />
      {loading == true ? (
        <Loading />
      ) : (
        <BodyBox>
          <div className='my-[80px] flex items-center gap-[40px] mobile:block mobile:mt-[30px]'>
            <div className='hidden flex mobile:mb-[50px] mobile:justify-center mobile:flex'>
              <div onClick={() => onCalendarOpen()}>
				<Calendar
					type='date'
					disabledDates={disabledDates}
					availableDays={availableDays}
					clickReservationDate={clickReservationDate}
					onClose={onCalendarClose}
					onOpenCalendar={isOpenCalendar}
					preserveScroll={true}
				/>
				</div>

                 <div className='relative flex items-center justify-center p-[10px] pr-[20px] h-[46px] border border-[#a4a4a4] border-l-[0px] rounded-l-[0px] rounded-r-[7px]'>
                <div className={`flex-1 flex items-center justify-center gap-[10px]`}>
                  <div className='flex gap-[10px] cursor-pointer' onClick={handleEditClick}>
                    <img
                      src='/person.png'
                      alt='person'
                      width={25}
                      height={25}
                      className='ml-[10px]'
                    />
                    <span className='text-[16px] whitespace-nowrap cursor-pointer mobile:text-[14px]'>
                      {adultCount != 0 ? '성인' + adultCount : ''}
                      {kidCount != 0 ? ' 어린이' + kidCount : ''}
                      {petCount != 0 ? ' 반려견' + petCount : ''}
                      {adultCount == 0 && kidCount == 0 && petCount == 0 && '인원'}
                    </span>
                  </div>


                </div>
                {showEditBox && (
                      <>
                          <div className='absolute bg-[#ffffffbf] top-full right-0 origin-[-50%_-50%] border rounded-[5px] p-[5px] z-10'>
                          <div>
                            <div className='flex items-center justify-center pt-[5px] m-[10px]'>
                              <div className='flex items-center w-[50px] mobile:text-[14px]'>
                                성인
                              </div>
                              <div className='flex items-center w-[144px] h-[55px] bg-[#ffffff] border border-[1px] border-[#263725] rounded-[10px] py-[10px] justify-center mobile:w-[100px] mobile:h-[40px]'>
                                <div className='flex items-center'>
                                  <div
                                    className='flex w-[30px] h-[30px] justify-center items-center'
                                    onClick={adultMinusCountHandler}
                                  >
                                    <Image
                                      className='w-[35px] h-[35px] mobile:w-[25px] mobile:h-[25px]'
                                      src='/minusIcon.png'
                                      alt='마이너스 아이콘'
                                      width={isDesktop ? 35 : 25}
                                      height={isDesktop ? 35 : 25}
                                      style={{ cursor: 'pointer' }}
                                    />
                                  </div>
                                  <div className='flex items-center justify-center p-[10px] mobile:text-[14px]'>
                                    {adultCount}
                                  </div>
                                  <div
                                    className='flex w-[30px] h-[30px] justify-center items-center'
                                    onClick={adultPlusCountHandler}
                                  >
                                    <Image
                                      className='w-[35px] h-[35px] mobile:w-[25px] mobile:h-[25px]'
                                      src='/plusIcon.png'
                                      alt='플러스 아이콘'
                                      width={35}
                                      height={35}
                                      style={{ cursor: 'pointer' }}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className='flex items-center justify-center pt-[5px] m-[10px]'>
                              <div className='flex items-center w-[50px] mobile:text-[14px]'>
                                어린이
                              </div>
                              <div className='flex items-center w-[144px] h-[55px] bg-[#ffffff] border border-[1px] border-[#263725] rounded-[10px] py-[10px] justify-center mobile:w-[100px] mobile:h-[40px]'>
                                <div className='flex items-center'>
                                  <div
                                    className='flex w-[30px] h-[30px] justify-center items-center'
                                    onClick={kidMinusCountHandler}
                                  >
                                    <Image
                                      className='w-[35px] h-[35px] mobile:w-[25px] mobile:h-[25px]'
                                      src='/minusIcon.png'
                                      alt='마이너스 아이콘'
                                      width={35}
                                      height={35}
                                      style={{ cursor: 'pointer' }}
                                    />
                                  </div>
                                  <div className='flex items-center justify-center p-[10px] mobile:text-[14px]'>
                                    {kidCount}
                                  </div>
                                  <div
                                    className='flex w-[30px] h-[30px] justify-center items-center'
                                    onClick={kidPlusCountHandler}
                                  >
                                    <Image
                                      className='w-[35px] h-[35px] mobile:w-[25px] mobile:h-[25px]'
                                      src='/plusIcon.png'
                                      alt='플러스 아이콘'
                                      width={35}
                                      height={35}
                                      style={{ cursor: 'pointer' }}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                            {isPet && (
                              <div className='flex items-center justify-center pt-[5px] m-[10px]'>
                                <div className='flex items-center w-[50px] mobile:text-[14px]'>
                                  반려견
                                </div>
                                <div
                                  className='flex items-center w-[144px] h-[55px] bg-[#ffffff] border border-[1px] border-[#263725] rounded-[10px] py-[10px] justify-center mobile:w-[100px] mobile:h-[40px]'
                                  style={{
                                    backgroundColor: isPet ? '#ffffff' : '#b3b3b3',
                                  }}
                                >
                                  <div className='flex items-center'>
                                    <div
                                      className='flex w-[30px] h-[30px] justify-center items-center'
                                      onClick={() => isPet && petMinusCountHandler()}
                                    >
                                      <Image
                                        className='w-[35px] h-[35px] mobile:w-[25px] mobile:h-[25px]'
                                        src='/minusIcon.png'
                                        alt='마이너스 아이콘'
                                        width={35}
                                        height={35}
                                        style={{ cursor: 'pointer' }}
                                      />
                                    </div>
                                    <div className='flex items-center justify-center p-[10px] mobile:text-[14px]'>
                                      {petCount}
                                    </div>
                                    <div
                                      className='flex w-[30px] h-[30px] justify-center items-center'
                                      onClick={() => isPet && petPlusCountHandler()}
                                    >
                                      <Image
                                        className='w-[35px] h-[35px] mobile:w-[25px] mobile:h-[25px]'
                                        src='/plusIcon.png'
                                        alt='플러스 아이콘'
                                        width={35}
                                        height={35}
                                        style={{ cursor: 'pointer' }}
                                      />
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                          <div
                            className='w-[100px] p-[7px] float-right text-center border border-black rounded-[25px] mb-[10px] mr-[10px] bg-[#000000] text-[#ffffff] cursor-pointer
                          mobile:text-[14px] mobile:w-[60px] moblie:h-[32px] mobile:flex mobile:justify-center mobile:items-center'
                            onClick={handleReSelectClick}
                          >
                            적용
                          </div>
                        </div>
                      </>
                    )}
              </div>
            </div>
            <div>
              <div className='text-[28px] leading-[28px] mobile:text-[24px]'>
                {partnerStoreData?.storeName}
              </div>
              <div className='text-[14px] mobile:text-[12px]'>
                {partnerStoreData?.storeEnglishName}
              </div>
            </div>
            <div className='flex mobile:mb-[50px] mobile:justify-center mobile:hidden'>
				<div onClick={() => onCalendarOpen()}>
				<Calendar
					type='date'
					disabledDates={disabledDates}
					availableDays={availableDays}
					clickReservationDate={clickReservationDate}
					onClose={onCalendarClose}
					onOpenCalendar={isOpenCalendar}
					preserveScroll={true}
				/>
				</div>
			  <div className={`relative top-[10px]`}>
                    {showEditBox && (
                      <>
                        <div className='absolute bg-[#ffffffbf] top-[50px] origin-[-50%_-50%] border rounded-[5px] p-[5px] z-10 mobile:left-[-100px]'>
                          <div>
                            <div className='flex items-center justify-center pt-[5px] m-[10px]'>
                              <div className='flex items-center w-[50px]'>성인</div>
                              <div className='flex items-center w-[144px] h-[55px] bg-[#ffffff] border border-[1px] border-[#263725] rounded-[10px] py-[10px] justify-center'>
                                <div className='flex items-center'>
                                  <div
                                    className='flex w-[30px] h-[30px]'
                                    onClick={adultMinusCountHandler}
                                  >
                                    <Image
                                      src='/minusIcon.png'
                                      alt='마이너스 아이콘'
                                      width={35}
                                      height={35}
                                      style={{ cursor: 'pointer' }}
                                    />
                                  </div>
                                  <div className='flex items-center justify-center p-[10px]'>
                                    {adultCount}
                                  </div>
                                  <div
                                    className='flex w-[30px] h-[30px]'
                                    onClick={adultPlusCountHandler}
                                  >
                                    <Image
                                      src='/plusIcon.png'
                                      alt='플러스 아이콘'
                                      width={35}
                                      height={35}
                                      style={{ cursor: 'pointer' }}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className='flex items-center justify-center pt-[5px] m-[10px]'>
                              <div className='flex items-center w-[50px]'>어린이</div>
                              <div className='flex items-center w-[144px] h-[55px] bg-[#ffffff] border border-[1px] border-[#263725] rounded-[10px] py-[10px] justify-center'>
                                <div className='flex items-center'>
                                  <div
                                    className='flex w-[30px] h-[30px]'
                                    onClick={kidMinusCountHandler}
                                  >
                                    <Image
                                      src='/minusIcon.png'
                                      alt='마이너스 아이콘'
                                      width={35}
                                      height={35}
                                      style={{ cursor: 'pointer' }}
                                    />
                                  </div>
                                  <div className='flex items-center justify-center p-[10px]'>
                                    {kidCount}
                                  </div>
                                  <div
                                    className='flex w-[30px] h-[30px]'
                                    onClick={kidPlusCountHandler}
                                  >
                                    <Image
                                      src='/plusIcon.png'
                                      alt='플러스 아이콘'
                                      width={35}
                                      height={35}
                                      style={{ cursor: 'pointer' }}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>

                            {isPet && (
                              <div className='flex items-center justify-center pt-[5px] m-[10px]'>
                                <div className='flex items-center w-[50px]'>반려견</div>
                                <div
                                  className='flex items-center w-[144px] h-[55px] bg-[#ffffff] border border-[1px] border-[#263725] rounded-[10px] py-[10px] justify-center'
                                  style={{
                                    backgroundColor: isPet ? '#ffffff' : '#b3b3b3',
                                  }}
                                >
                                  <div className='flex items-center'>
                                    <div
                                      className='flex w-[30px] h-[30px]'
                                      onClick={() => isPet && petMinusCountHandler()}
                                    >
                                      <Image
                                        src='/minusIcon.png'
                                        alt='마이너스 아이콘'
                                        width={35}
                                        height={35}
                                        style={{ cursor: 'pointer' }}
                                      />
                                    </div>
                                    <div className='flex items-center justify-center p-[10px]'>
                                      {petCount}
                                    </div>
                                    <div
                                      className='flex w-[30px] h-[30px]'
                                      onClick={() => isPet && petPlusCountHandler()}
                                    >
                                      <Image
                                        src='/plusIcon.png'
                                        alt='플러스 아이콘'
                                        width={35}
                                        height={35}
                                        style={{ cursor: 'pointer' }}
                                      />
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                          <div
                            className='w-[100px] p-[7px] float-right text-center border border-black rounded-[25px] mb-[10px] mr-[10px] bg-[#000000] text-[#ffffff] cursor-pointer'
                            onClick={handleReSelectClick}
                          >
                            적용
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                 <div className='flex items-center justify-center p-[10px] pr-[20px] h-[46px] border border-[#a4a4a4] border-l-[0px] rounded-l-[0px] rounded-r-[7px]'>
                <div className={`flex-1 flex items-center justify-center gap-[10px]`}>
                  <div className='flex gap-[10px] cursor-pointer' onClick={handleEditClick}>
                    <img
                      src='/person.png'
                      alt='person'
                      width={25}
                      height={25}
                      className='ml-[10px]'
                    />
                    <span className='text-[16px] whitespace-nowrap cursor-pointer mobile:text-[12px]'>
                      {adultCount != 0 ? '성인 ' + adultCount : ''}
                      {kidCount != 0 ? ' 어린이 ' + kidCount : ''}
                      {petCount != 0 ? ' 반려견 ' + petCount : ''}
                      {adultCount == 0 && kidCount == 0 && petCount == 0 && '인원'}
                    </span>
                  </div>
                  
                </div>
              </div>
            </div>
          </div>

          <RoomTypeList
            title='객실타입'
            roomList={roomList}
            stayCount={stayCount}
            period={{
              start: headerPeriod != null ? headerPeriod.start : startDate,
              end: headerPeriod != null ? headerPeriod.end : endDate,
            }}
            headcount={{ adult: adultCount, kid: kidCount, pet: petCount }}
          />


          <div className='relative mt-[200px] mobile:mt-[110px] mb-[105px] px-[20px] mobile:px-[1px]'>
            <Image
              className='w-full max-h-[1080px] object-cover'
              src={topImage?.imageUrl || '/images/stay-default-image.svg'}
              alt='숙소 대표 이미지'
              width={1920}
              height={1080}
              sizes='100vw'
            />
            <div className='flex flex-col justify-end py-[18px] text-black overflow-hidden'>
              <div className='intro mb-[20px]'></div>
              <div className='accommodation px-4 max-w-[1280px] mx-auto'>
                {partnerStoreData?.storyContent && (
                  <div
                    className='w-full min-h-[220px] m-auto leading-[20px] mobile:text-[14px]'
                    dangerouslySetInnerHTML={{
                      __html: xss(String(partnerStoreData?.storyContent)),
                    }}
                  ></div>
                )}
              </div>
            </div>
          </div>

          <div className="w-full pt-[10px] mobile:pt-[0px]">
            <SwiperBox
              freeMode={false}
              slidesPerView={getSlidesPerView()}
              scrollbarHide={true}
              type='A'
              category='feature'
              onSwiper={onSwiper}
              onSwiperIndex={onSwiperIndex}
              list={featureData?.map((item: FeatureItem, idx: number) => <CourseItemBox key={idx} data={item} />)}
            />
          </div>

          <div className='map mt-[50px] mb-[50px]'>
            <div className='w-full mt-[50px]'>
              <div style={{ marginBottom: '15px' }}>
                <div className='flex-col items-center font-[16px] gap-[5px] md:flex-row'>
                  <p>
                    <strong className='text-[20px] font-medium mobile:text-[16px]'>
                      {partnerStoreData?.storeName}
                    </strong>
                  </p>
                  <div className='font-light text-darkgray mobile:text-[14px]'>
                    {partnerStoreData?.address}
                  </div>
                </div>
              </div>

              <div className='tourist_spot_map_container'>
                <div className='map_wrap'>
                  <div
                    id='map'
                    style={{
                      // 지도의 크기
                      width: '100%',
                      height: '350px',
                    }}
                  ></div>
                </div>
              </div>
            </div>
          </div>

          <div className='grid grid-cols-3 mobile:grid-cols-1 gap-x-[80px] gap-y-[40px] mobile:gap-y-[20px] px-[10px] mobile:px-[4px] mb-[100px]'>
            {touristSpotData?.map((item: TouristSpot, index: number) => (
              <div key={index} className='cursor-pointer' onClick={() => handleSelectTour(item,index+1)}>
                <DestinationInfoBox key={index + 1} id={index + 1} item={item} />
              </div>
            ))}
          </div>

          <div className='mb-[210px]'>
            <TabInfoBox guideData={guideData} refundPolicyInfoList={refundPolicyInfoList} />
          </div>
        </BodyBox>
      )}
    </div>
  );
};

const Stay = (props: { params: { stayId: string } }) => {
  return (
    <Suspense fallback={null}>
      <StayContent {...props} />
    </Suspense>
  );
};

export default Stay;
